/**
 * Selective Invalidation Service
 * 
 * Advanced cache invalidation with granular control over what gets cleared.
 * Integrates with namespaced storage to provide surgical cache management
 * based on meta-version changes, community context, and feature scope.
 * 
 * Phase 4 implementation - selective and intelligent cache invalidation
 */

import { QueryClient } from '@tanstack/react-query';
import { 
  clearNamespaceStorage, 
  getNamespaceKeys,
  type StorageNamespace 
} from './namespacedStorageService';
import { 
  invalidateBrandingCache,
  type BrandingInvalidationResult 
} from './brandingCacheIntegration';
import { 
  detectCommunityContext, 
  type CommunityContext 
} from './communityAwareCacheInvalidation';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Invalidation scope defines what should be cleared
 */
export type InvalidationScope = 
  | 'global'           // Clear everything
  | 'community'        // Clear current community only
  | 'branding'         // Clear branding-related caches only
  | 'auth'            // Clear auth-related caches only
  | 'user-preferences' // Clear user preferences only
  | 'onboarding'      // Clear onboarding data only
  | 'custom';         // Custom scope with specific patterns

/**
 * Invalidation strategy determines how aggressive the clearing is
 */
export type InvalidationStrategy = 
  | 'conservative'  // Clear only what's definitely stale
  | 'moderate'      // Clear likely stale data
  | 'aggressive'    // Clear everything in scope
  | 'nuclear';      // Clear everything, no exceptions

/**
 * Invalidation options for fine-grained control
 */
export interface SelectiveInvalidationOptions {
  scope: InvalidationScope;
  strategy: InvalidationStrategy;
  preserveAuth?: boolean;
  preserveUserPreferences?: boolean;
  preserveTheme?: boolean;
  communityContext?: CommunityContext;
  metaVersions?: string[];
  customPatterns?: {
    include?: string[];
    exclude?: string[];
  };
  dryRun?: boolean; // Preview what would be cleared
}

/**
 * Invalidation result with detailed breakdown
 */
export interface SelectiveInvalidationResult {
  success: boolean;
  scope: InvalidationScope;
  strategy: InvalidationStrategy;
  storageKeysCleared: string[];
  queryKeysCleared: string[];
  brandingInvalidated: boolean;
  communityContext: CommunityContext;
  preservedKeys: string[];
  errors: string[];
  durationMs: number;
  dryRun: boolean;
}

/**
 * Get storage keys to clear based on scope and strategy
 */
async function getKeysToInvalidate(
  options: SelectiveInvalidationOptions
): Promise<{
  storageKeys: string[];
  preservedKeys: string[];
  errors: string[];
}> {
  const result = {
    storageKeys: [] as string[],
    preservedKeys: [] as string[],
    errors: [] as string[],
  };

  try {
    const communityContext = options.communityContext || detectCommunityContext();
    const currentEnv = isDevelopment ? 'dev' : 'prod';

    switch (options.scope) {
      case 'global':
        // Clear everything based on strategy
        if (options.strategy === 'nuclear') {
          // Get ALL terrarium keys
          const allKeysResult = getNamespaceKeys({});
          if (allKeysResult.success && allKeysResult.data) {
            result.storageKeys.push(...allKeysResult.data);
          }
        } else {
          // Clear by environment and optionally meta-version
          const namespaceResult = getNamespaceKeys({ env: currentEnv });
          if (namespaceResult.success && namespaceResult.data) {
            result.storageKeys.push(...namespaceResult.data);
          }
        }
        break;

      case 'community':
        // Clear current community only
        const communityResult = getNamespaceKeys({
          env: currentEnv,
          community: communityContext.hostname,
        });
        if (communityResult.success && communityResult.data) {
          result.storageKeys.push(...communityResult.data);
        }
        break;

      case 'branding':
        // Clear branding-specific keys
        const brandingPatterns = ['community_branding', 'critical_branding'];
        for (const pattern of brandingPatterns) {
          const keys = Object.keys(localStorage).filter(key => 
            key.includes(pattern)
          );
          result.storageKeys.push(...keys);
        }
        break;

      case 'auth':
        // Clear auth-related keys
        const authPatterns = ['auth_state', 'supabase.auth'];
        for (const pattern of authPatterns) {
          const keys = Object.keys(localStorage).filter(key => 
            key.includes(pattern)
          );
          result.storageKeys.push(...keys);
        }
        break;

      case 'user-preferences':
        // Clear user preferences only
        const prefsResult = getNamespaceKeys({
          env: currentEnv,
          global: true,
        });
        if (prefsResult.success && prefsResult.data) {
          const prefsKeys = prefsResult.data.filter(key => 
            key.includes('user_preferences') || key.includes('theme_preference')
          );
          result.storageKeys.push(...prefsKeys);
        }
        break;

      case 'onboarding':
        // Clear onboarding data
        const onboardingKeys = Object.keys(localStorage).filter(key =>
          key.includes('onboarding') || key.includes('member_onboarding')
        );
        result.storageKeys.push(...onboardingKeys);
        break;

      case 'custom':
        // Use custom patterns
        if (options.customPatterns?.include) {
          for (const pattern of options.customPatterns.include) {
            const keys = Object.keys(localStorage).filter(key =>
              key.includes(pattern)
            );
            result.storageKeys.push(...keys);
          }
        }
        break;
    }

    // Remove excluded patterns
    if (options.customPatterns?.exclude) {
      const excludePatterns = options.customPatterns.exclude;
      result.storageKeys = result.storageKeys.filter(key =>
        !excludePatterns.some(pattern => key.includes(pattern))
      );
    }

    // Handle preservation options
    if (options.preserveAuth) {
      const authKeys = result.storageKeys.filter(key =>
        key.includes('auth') || key.includes('supabase.auth')
      );
      result.storageKeys = result.storageKeys.filter(key =>
        !authKeys.includes(key)
      );
      result.preservedKeys.push(...authKeys);
    }

    if (options.preserveUserPreferences) {
      const prefKeys = result.storageKeys.filter(key =>
        key.includes('user_preferences') || key.includes('theme_preference')
      );
      result.storageKeys = result.storageKeys.filter(key =>
        !prefKeys.includes(key)
      );
      result.preservedKeys.push(...prefKeys);
    }

    if (options.preserveTheme) {
      const themeKeys = result.storageKeys.filter(key =>
        key.includes('theme')
      );
      result.storageKeys = result.storageKeys.filter(key =>
        !themeKeys.includes(key)
      );
      result.preservedKeys.push(...themeKeys);
    }

    // Remove duplicates
    result.storageKeys = [...new Set(result.storageKeys)];
    result.preservedKeys = [...new Set(result.preservedKeys)];

  } catch (error) {
    result.errors.push(`Failed to get keys to invalidate: ${error}`);
  }

  return result;
}

/**
 * Get React Query keys to clear based on scope
 */
function getQueryKeysToInvalidate(
  queryClient: QueryClient | null,
  options: SelectiveInvalidationOptions
): string[] {
  if (!queryClient) return [];

  const queryKeys: string[] = [];
  const communityContext = options.communityContext || detectCommunityContext();

  switch (options.scope) {
    case 'global':
      // Clear all queries
      queryClient.clear();
      queryKeys.push('*');
      break;

    case 'community':
      // Clear community-specific queries
      const communityPatterns = [
        ['community', communityContext.slug],
        ['branding', communityContext.hostname],
        ['jobs', communityContext.id],
        ['events', communityContext.id],
        ['channels', communityContext.id],
      ];
      
      for (const pattern of communityPatterns) {
        queryClient.removeQueries({ queryKey: pattern });
        queryKeys.push(pattern.join(':'));
      }
      break;

    case 'branding':
      // Clear branding queries only
      queryClient.removeQueries({ 
        predicate: (query) => {
          return Array.isArray(query.queryKey) && 
                 query.queryKey.length > 0 && 
                 query.queryKey[0] === 'branding';
        }
      });
      queryKeys.push('branding:*');
      break;

    case 'auth':
      // Clear auth-related queries
      const authPatterns = ['auth', 'user', 'profile', 'session'];
      for (const pattern of authPatterns) {
        queryClient.removeQueries({
          predicate: (query) => {
            return Array.isArray(query.queryKey) && 
                   query.queryKey.length > 0 && 
                   query.queryKey[0] === pattern;
          }
        });
        queryKeys.push(`${pattern}:*`);
      }
      break;

    // Other scopes don't typically involve React Query
    default:
      break;
  }

  return queryKeys;
}

/**
 * Perform selective cache invalidation
 */
export async function performSelectiveInvalidation(
  queryClient: QueryClient | null,
  options: SelectiveInvalidationOptions
): Promise<SelectiveInvalidationResult> {
  const startTime = performance.now();
  const communityContext = options.communityContext || detectCommunityContext();

  if (isDevelopment) {
    console.log('SelectiveInvalidation: Starting', { options, communityContext });
  }

  const result: SelectiveInvalidationResult = {
    success: false,
    scope: options.scope,
    strategy: options.strategy,
    storageKeysCleared: [],
    queryKeysCleared: [],
    brandingInvalidated: false,
    communityContext,
    preservedKeys: [],
    errors: [],
    durationMs: 0,
    dryRun: options.dryRun || false,
  };

  try {
    // Get keys to invalidate
    const keysAnalysis = await getKeysToInvalidate(options);
    result.preservedKeys = keysAnalysis.preservedKeys;
    result.errors.push(...keysAnalysis.errors);

    if (options.dryRun) {
      // Dry run - just preview what would be cleared
      result.storageKeysCleared = keysAnalysis.storageKeys;
      result.queryKeysCleared = getQueryKeysToInvalidate(queryClient, options);
      result.success = true;
      
      if (isDevelopment) {
        console.log('SelectiveInvalidation: Dry run completed', {
          wouldClear: result.storageKeysCleared.length,
          wouldPreserve: result.preservedKeys.length,
        });
      }
    } else {
      // Actual invalidation
      
      // Clear localStorage keys
      for (const key of keysAnalysis.storageKeys) {
        try {
          localStorage.removeItem(key);
          result.storageKeysCleared.push(key);
        } catch (error) {
          result.errors.push(`Failed to remove ${key}: ${error}`);
        }
      }

      // Clear React Query keys
      result.queryKeysCleared = getQueryKeysToInvalidate(queryClient, options);

      // Handle branding invalidation if relevant
      if (options.scope === 'global' || options.scope === 'community' || options.scope === 'branding') {
        if (queryClient) {
          try {
            const brandingResult = await invalidateBrandingCache(
              queryClient,
              communityContext.hostname,
              `Selective invalidation: ${options.scope}`
            );
            result.brandingInvalidated = brandingResult.success;
            if (!brandingResult.success) {
              result.errors.push(...brandingResult.errors);
            }
          } catch (error) {
            result.errors.push(`Branding invalidation failed: ${error}`);
          }
        }
      }

      result.success = result.errors.length === 0;
    }
  } catch (error) {
    result.errors.push(`Selective invalidation failed: ${error}`);
  }

  result.durationMs = performance.now() - startTime;

  if (isDevelopment) {
    console.log('SelectiveInvalidation: Completed', {
      ...result,
      keysCleared: result.storageKeysCleared.length,
      keysPreserved: result.preservedKeys.length,
    });
  }

  return result;
}

/**
 * Preset invalidation configurations for common scenarios
 */
export const INVALIDATION_PRESETS: Record<string, SelectiveInvalidationOptions> = {
  // Quick refresh - minimal clearing
  quickRefresh: {
    scope: 'branding',
    strategy: 'conservative',
    preserveAuth: true,
    preserveUserPreferences: true,
    preserveTheme: true,
  },

  // Community switch - clear community-specific data
  communitySwitch: {
    scope: 'community',
    strategy: 'moderate',
    preserveAuth: true,
    preserveUserPreferences: true,
    preserveTheme: true,
  },

  // Database reset - comprehensive clearing
  databaseReset: {
    scope: 'global',
    strategy: 'aggressive',
    preserveAuth: true,
    preserveUserPreferences: true,
  },

  // Development reset - nuclear option
  developmentReset: {
    scope: 'global',
    strategy: 'nuclear',
    preserveAuth: false,
    preserveUserPreferences: false,
  },

  // Auth logout - clear auth but preserve other data
  authLogout: {
    scope: 'auth',
    strategy: 'aggressive',
    preserveAuth: false,
    preserveUserPreferences: true,
    preserveTheme: true,
  },

  // Theme change - minimal clearing
  themeChange: {
    scope: 'custom',
    strategy: 'conservative',
    customPatterns: {
      include: ['theme', 'branding'],
    },
    preserveAuth: true,
    preserveUserPreferences: true,
  },

  // Onboarding reset - clear onboarding data only
  onboardingReset: {
    scope: 'onboarding',
    strategy: 'aggressive',
    preserveAuth: true,
    preserveUserPreferences: true,
    preserveTheme: true,
  },
};

/**
 * Convenience function for common invalidation scenarios
 */
export async function invalidateWithPreset(
  queryClient: QueryClient | null,
  presetName: keyof typeof INVALIDATION_PRESETS,
  overrides?: Partial<SelectiveInvalidationOptions>
): Promise<SelectiveInvalidationResult> {
  const preset = INVALIDATION_PRESETS[presetName];
  if (!preset) {
    throw new Error(`Unknown invalidation preset: ${presetName}`);
  }

  const options = { ...preset, ...overrides };
  return performSelectiveInvalidation(queryClient, options);
}

/**
 * Development utility to preview invalidation
 */
export async function previewInvalidation(
  queryClient: QueryClient | null,
  options: SelectiveInvalidationOptions
): Promise<SelectiveInvalidationResult> {
  return performSelectiveInvalidation(queryClient, {
    ...options,
    dryRun: true,
  });
}